import { useMemo } from "react";

import { usePinned } from "fond/views/Accounts/hooks/usePinned";

interface UseSortedAccountsOptions {
  /** If true, only return pinned accounts (may return empty array if nothing is pinned) */
  pinnedOnly?: boolean;
}

/**
 * Custom hook to sort pinned accounts.
 * Non pinned accounts are excluded if pinned accounts exist, otherwise all accounts are shown.
 * @param accounts - Array of accounts to sort
 * @param options - Optional configuration
 * @param options.pinnedOnly - If true, only return pinned accounts (may return empty array)
 */
export const useSortedAccounts = <T extends { ID: string }>(accounts: T[], options?: UseSortedAccountsOptions): T[] => {
  const { pinned: pinnedAccountIds } = usePinned();
  const { pinnedOnly = false } = options ?? {};

  return useMemo(() => {
    const pinnedAccounts = accounts.filter((account) => pinnedAccountIds.includes(account.ID));

    // If pinnedOnly option is set, always return only pinned accounts
    if (pinnedOnly) {
      return pinnedAccounts.sort((a, b) => {
        const indexA = pinnedAccountIds.indexOf(a.ID);
        const indexB = pinnedAccountIds.indexOf(b.ID);
        return indexA - indexB;
      });
    }

    // If there are pinned accounts, show only those sorted by pinned order
    // Otherwise show all accounts
    let displayAccounts = pinnedAccounts.length > 0 ? pinnedAccounts : accounts;

    // Sort pinned accounts based on their order in pinnedAccountIds array
    if (pinnedAccounts.length > 0) {
      displayAccounts = displayAccounts.sort((a, b) => {
        const indexA = pinnedAccountIds.indexOf(a.ID);
        const indexB = pinnedAccountIds.indexOf(b.ID);
        return indexA - indexB;
      });
    }

    return displayAccounts;
  }, [accounts, pinnedAccountIds, pinnedOnly]);
};
